/**
 * @file ServiceControlPanel.jsx
 * @description The SERVICE department's dashboard — its own control panel,
 *              because service's job is not the rental fleet's job.
 *
 *              Row 1  the pipeline: eight flat metric tiles. Only the tiles
 *                     that MEAN something right now carry colour (breakdown
 *                     red, ready-for-rent blue, holding orange) — the rest
 *                     stay grey so the eye lands on what needs action.
 *              Row 2  open work (ring), jobs completed by week (bars), and a
 *                     full-bleed map of machines that are down.
 *              Row 3  errors reported, machines gone silent, and the request
 *                     queue.
 *              Row 4  the floor — every machine the team is holding.
 *
 *              Data: GET /api/service/dashboard (one call, 30s server cache).
 *              Styling is Tailwind (the app ships it alongside MUI) so this
 *              matches the agreed dashboard design exactly.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS, BarElement, CategoryScale, LinearScale,
    Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import serviceDashboardService from '../../services/serviceDashboardService';
import { formatToIST } from '../../utils/dataUtils';
import { machineLabel } from '../../utils/machineLabel';

ChartJS.register(BarElement, CategoryScale, LinearScale, ChartTooltip, Legend);

// Where the map sits when there is nothing to plot — the operating region, so
// an empty map still looks like this fleet's map.
const FLEET_CENTER = [17.5, 78.5];

/**
 * The eight pipeline tiles. `tone` decides the emphasis: 'alert' (red) and
 * 'hold' (solid orange) shout, 'ready' (blue) is good news, 'plain' is grey.
 * A shouting tile falls back to grey at zero — nothing to shout about.
 */
const TILES = [
    { key: 'in_service', label: 'In Service', tone: 'plain' },
    { key: 'maintenance', label: 'In Maintenance', tone: 'plain' },
    { key: 'breakdown', label: 'Breakdown', tone: 'alert' },
    { key: 'ready_for_rent', label: 'Ready for Rent', tone: 'ready' },
    { key: 'holding', label: 'Holding', tone: 'hold' },
    { key: 'waiting_check', label: 'Waiting Check', tone: 'plain' },
    { key: 'in_check', label: 'In Check', tone: 'plain' },
    { key: 'due_next_month', label: 'Due Next Month', tone: 'plain' },
];

const TILE_STYLES = {
    plain: { box: 'bg-gray-200', value: 'text-gray-800', label: 'text-gray-500' },
    alert: { box: 'bg-red-100 border-b-4 border-red-500', value: 'text-red-600', label: 'text-red-500' },
    ready: { box: 'bg-blue-100', value: 'text-blue-600', label: 'text-blue-500' },
    hold: { box: 'bg-orange-600 shadow-md', value: 'text-white', label: 'text-white opacity-90' },
};

// Status dot + label for the floor table.
const STATUS_META = {
    IN_PROGRESS: { label: 'In Progress', dot: 'bg-blue-600' },
    WAITING_SPARES: { label: 'Holding — Parts', dot: 'bg-orange-500' },
    ASSIGNED: { label: 'Assigned', dot: 'bg-cyan-600' },
    OPEN: { label: 'Open', dot: 'bg-gray-400' },
    RESOLVED: { label: 'Resolved', dot: 'bg-purple-600' },
    CLOSED: { label: 'Closed', dot: 'bg-green-600' },
};

const fmtIST = (ts, withTime = true) => {
    if (!ts) return '—';
    const out = formatToIST(ts, withTime ? 'dd MMM · HH:mm' : 'dd MMM');
    return out === 'N/A' || out === 'Invalid Date' ? '—' : out;
};

/** "4h 12m" — how long a machine has been quiet. */
const elapsed = (ts) => {
    if (!ts) return 'never';
    const ms = Date.now() - new Date(ts).getTime();
    if (!Number.isFinite(ms) || ms < 0) return '—';
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    return h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h ${String(m).padStart(2, '0')}m`;
};

/** The ⋮ button the design puts in each card header. */
const CardMenu = ({ onClick, title }) => (
    <button type="button" onClick={onClick} title={title}
        className="text-gray-400 hover:text-gray-600">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
    </button>
);

const ServiceControlPanel = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Clicking an error row opens THAT machine's Visualization pinned to the
    // error's exact frame — the same /mewp-detail deep link the Daily Report
    // uses (?view=viz&date&at&alarm: the day loads, the frame nearest the
    // timestamp is selected, and a banner names the error you arrived on).
    // A machine with no timestamp (never reported) opens plain.
    const openErrorViz = (equipmentId, at, alarmName) => {
        if (!equipmentId) return;
        const t = at ? new Date(at) : null;
        if (!t || isNaN(t)) { navigate(`/mewp-detail/${encodeURIComponent(equipmentId)}`); return; }
        const day = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
        const q = new URLSearchParams({ view: 'viz', date: day, at: t.toISOString() });
        if (alarmName) q.set('alarm', alarmName);
        navigate(`/mewp-detail/${encodeURIComponent(equipmentId)}?${q.toString()}`);
    };

    const load = useCallback(async (first = false) => {
        try {
            const r = await serviceDashboardService.getDashboard();
            setData(r);
            setError('');
        } catch (e) {
            if (first) setError(e.message || 'Failed to load the service dashboard');
        } finally {
            if (first) setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(true);
        const id = setInterval(() => load(false), 60_000);
        return () => clearInterval(id);
    }, [load]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="h-8 w-8 rounded-full border-2 border-gray-200 border-t-blue-700 animate-spin" />
            </div>
        );
    }
    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 text-red-700 rounded p-4 flex items-center justify-between">
                    <span className="text-sm">{error}</span>
                    <button onClick={() => { setLoading(true); load(true); }}
                        className="text-xs font-bold uppercase px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const c = data?.counts || {};
    const md = data?.maintenance_due || {};        // overdue / due / upcoming split
    const p = data?.pipeline || {};
    const weeks = data?.weekly_completions || [];
    const siteThreshold = data?.site_threshold || [];
    const dtcs = data?.dtcs || [];
    const deviceAlerts = data?.device_alerts || [];
    const silent = data?.silent_machines || [];
    const workList = data?.work_list || [];
    const breakdowns = data?.breakdowns || [];
    const mapped = breakdowns.filter(b => Number.isFinite(Number(b.lat)) && Number.isFinite(Number(b.lng))
        && Number(b.lat) !== 0 && Number(b.lng) !== 0);
    const openTotal = (p.in_progress || 0) + (p.queued || 0) + (p.holding || 0) + (p.waiting_check || 0);

    // Bars are grey with the CURRENT week picked out in navy, so "where are we
    // this week" reads without a legend.
    const weeklyChart = {
        labels: weeks.map(w => fmtIST(w.week_start, false)),
        datasets: [{
            label: 'Jobs completed',
            data: weeks.map(w => w.completed),
            backgroundColor: weeks.map((_, i) => (i === weeks.length - 1 ? '#1e3a8a' : '#e5e7eb')),
            borderWidth: 0,
            barPercentage: 0.8,
            categoryPercentage: 0.9,
        }],
    };
    const weeklyOpts = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: true } },
        scales: {
            x: { grid: { display: false, drawBorder: false }, ticks: { font: { size: 10 }, color: '#6b7280' } },
            y: { display: false, grid: { display: false }, beginAtZero: true },
        },
        layout: { padding: 0 },
    };

    return (
        <div className="p-4 md:p-6 bg-gray-100 min-h-full animate-fade-in">
            {/* Fluid: the panel fills whatever screen it is on (capped only on
                ultra-wide monitors) and every fixed height below is a clamp()
                of the viewport, so zooming out or a 4K display scales the
                cards instead of leaving them in a narrow column. */}
            <main className="w-full max-w-[1920px] mx-auto flex flex-col gap-4 md:gap-6">

                {/* Refresh — no page title, the top bar already names the page */}
                <div className="flex items-end justify-end">
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-500">Updated {fmtIST(data?.generated_at)}</span>
                        <button onClick={() => load(false)} title="Refresh"
                            className="p-1 text-gray-500 hover:bg-gray-200 rounded">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Row 1 — pipeline tiles */}
                <section className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3 md:gap-4">
                    {TILES.map(tile => {
                        const value = Number(c[tile.key] || 0);
                        // Machines already past their service are the urgent part
                        // of "due next month" — the tile turns red and says how
                        // many, rather than burying it in the total.
                        const overdue = tile.key === 'due_next_month' ? Number(md.overdue || 0) : 0;
                        const tone = tile.key === 'due_next_month' && overdue > 0
                            ? 'alert'
                            : (value === 0 && tile.tone !== 'ready' ? 'plain' : tile.tone);
                        const s = TILE_STYLES[tone];
                        const hint = tile.key === 'due_next_month' && value > 0
                            ? `${md.overdue || 0} overdue · ${md.due || 0} due · ${md.upcoming || 0} scheduled within 30 days`
                            : undefined;
                        return (
                            <div key={tile.key} className={`${s.box} rounded p-4 text-center`} title={hint}>
                                <div className={`text-[clamp(1.6rem,2.2vw,2.75rem)] leading-tight font-bold ${s.value}`}>
                                    {value.toLocaleString('en-IN')}
                                </div>
                                <div className={`text-[10px] font-semibold ${s.label} uppercase tracking-wider mt-1`}>
                                    {tile.label}
                                </div>
                                {overdue > 0 && (
                                    <div className={`text-[10px] font-bold ${s.label} mt-0.5`}>
                                        {overdue} overdue
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </section>

                {/* Row 2 — open work, weekly completions, breakdown map */}
                <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">

                    {/* Open work — the ring always draws, so zero reads as zero */}
                    <div className="bg-white rounded shadow p-5">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-sm font-semibold text-gray-700">Open Work Right Now</h2>
                            <CardMenu title="Open job cards" onClick={() => navigate('/service/job-cards')} />
                        </div>
                        <div className="flex justify-center items-center h-[clamp(10rem,22vh,19rem)] relative">
                            <div className={`w-[clamp(8rem,16vh,14rem)] h-[clamp(8rem,16vh,14rem)] rounded-full border-8 ${openTotal > 0 ? 'border-blue-900' : 'border-gray-100'} flex items-center justify-center`}>
                                <div className="text-center">
                                    <span className="text-[clamp(1.75rem,3.5vh,3rem)] font-bold text-gray-800">{openTotal}</span>
                                    <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">Open</span>
                                </div>
                            </div>
                        </div>
                        {openTotal > 0 && (
                            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] text-gray-500 -mt-2">
                                <span>{p.in_progress || 0} in progress</span>
                                <span>{p.queued || 0} queued</span>
                                <span>{p.holding || 0} holding</span>
                                <span>{p.waiting_check || 0} waiting check</span>
                            </div>
                        )}
                    </div>

                    {/* Jobs completed — eight zero-filled weeks, latest in navy */}
                    <div className="bg-white rounded shadow p-5">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-sm font-semibold text-gray-700">Jobs Completed — By Week</h2>
                            <CardMenu title="Daily report" onClick={() => navigate('/service/daily-report')} />
                        </div>
                        <div className="h-[clamp(10rem,22vh,19rem)]">
                            <Bar data={weeklyChart} options={weeklyOpts} />
                        </div>
                    </div>

                    {/* Breakdowns — map bleeds to the card edges */}
                    <div className="bg-white rounded shadow p-5 relative overflow-hidden flex flex-col">
                        <div className="flex justify-between items-center mb-2 z-[500] relative">
                            <h2 className="text-sm font-semibold text-gray-700 bg-white/80 px-1 rounded">Breakdowns on Site</h2>
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${breakdowns.length ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                                {breakdowns.length}
                            </span>
                        </div>
                        <div className="flex-grow w-full -mx-5 -mb-5 relative min-h-[clamp(176px,26vh,22rem)]">
                            <MapContainer
                                center={mapped.length ? [mapped[0].lat, mapped[0].lng] : FLEET_CENTER}
                                zoom={mapped.length === 1 ? 9 : mapped.length ? 5 : 4}
                                style={{ height: '100%', width: '100%', minHeight: 'clamp(176px, 26vh, 22rem)' }}
                                scrollWheelZoom={false}
                            >
                                <TileLayer attribution="&copy; OpenStreetMap"
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                {mapped.map(b => (
                                    <CircleMarker key={b.equipment_id} center={[b.lat, b.lng]} radius={8}
                                        pathOptions={{
                                            color: b.reason === 'Breakdown' ? '#dc2626' : '#d97706',
                                            fillColor: b.reason === 'Breakdown' ? '#dc2626' : '#d97706',
                                            fillOpacity: 0.85, weight: 2,
                                        }}>
                                        <Popup>
                                            <div className="min-w-[170px]">
                                                <div className="font-bold text-sm">{machineLabel(b)}</div>
                                                <div className="text-xs text-gray-500">
                                                    {[b.brand, b.model].filter(Boolean).join(' ') || '—'}
                                                </div>
                                                <span className={`inline-block mt-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded ${b.reason === 'Breakdown' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                                                    {b.reason}
                                                </span>
                                                {b.site && <div className="text-xs mt-1">Site: {b.site}</div>}
                                                {b.job && <div className="text-xs text-gray-500">{b.job}</div>}
                                                <div className="text-[11px] text-gray-400">Position {fmtIST(b.position_at)}</div>
                                            </div>
                                        </Popup>
                                    </CircleMarker>
                                ))}
                            </MapContainer>
                            {mapped.length === 0 && (
                                <div className="absolute bottom-0 inset-x-0 z-[500] bg-white/90 py-1 text-center">
                                    <span className="text-[11px] text-gray-500">
                                        {breakdowns.length === 0
                                            ? 'No machines are down right now'
                                            : `${breakdowns.length} down · no GPS position on record`}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Row 3 — the three error origins + silence. Same 24h/active
                    windows the boxes' queries use; every row deep-links the
                    machine's Visualization at that error's exact frame. */}
                <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">

                    {/* Computed alarms: THRESH_* operating limits + geofence */}
                    <div className="bg-white rounded shadow p-5">
                        <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                            <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            M/C Site Threshold
                        </h2>
                        {siteThreshold.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">No threshold alarms in the last 24 hours.</p>
                        ) : (
                            <ul className="space-y-3 max-h-[clamp(14rem,32vh,30rem)] overflow-y-auto">
                                {siteThreshold.map((e, i) => (
                                    <li key={i} onClick={() => openErrorViz(e.equipment_id, e.created_at, e.alarm_name)}
                                        title="Open this machine's visualization at the error"
                                        className="text-xs cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-gray-800 truncate pr-2">{machineLabel(e)}</span>
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase shrink-0 ${
                                                e.severity === 'critical'
                                                    ? 'bg-red-100 text-red-700'
                                                    : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                                                {e.severity}
                                            </span>
                                        </div>
                                        <div className="text-[10px] text-gray-400 truncate" title={e.alarm_name}>{e.alarm_name}</div>
                                        {e.ops_raised_by && (
                                            <div className="text-[10px] font-semibold text-blue-700">Operations raised · {e.ops_raised_by}</div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* The real machine errors: J1939 fault codes off the vehicle CAN */}
                    <div className="bg-white rounded shadow p-5">
                        <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                            <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                    d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                            </svg>
                            DTC — Vehicle CAN
                        </h2>
                        {dtcs.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">No active fault codes from the CAN.</p>
                        ) : (
                            <ul className="space-y-3 max-h-[clamp(14rem,32vh,30rem)] overflow-y-auto">
                                {dtcs.map((d, i) => (
                                    <li key={i} onClick={() => openErrorViz(d.equipment_id, d.created_at, d.alarm_name)}
                                        title="Open this machine's visualization at the fault"
                                        className="text-xs cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-gray-800 truncate pr-2">{machineLabel(d)}</span>
                                            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase shrink-0 bg-orange-100 text-orange-700">
                                                {d.alarm_name}
                                            </span>
                                        </div>
                                        {d.message && (
                                            <div className="text-[10px] text-gray-400 truncate" title={d.message}>{d.message}</div>
                                        )}
                                        {d.ops_raised_by && (
                                            <div className="text-[10px] font-semibold text-blue-700">Operations raised · {d.ops_raised_by}</div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Silence — the 4h frame rule */}
                    <div className="bg-white rounded shadow p-5">
                        <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            No Frame Over {data?.silence_hours || 4}h
                        </h2>
                        {silent.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">Every machine is reporting.</p>
                        ) : (
                            <ul className="space-y-3 max-h-[clamp(14rem,32vh,30rem)] overflow-y-auto">
                                {silent.map((s, i) => (
                                    <li key={i} onClick={() => openErrorViz(s.equipment_id, s.last_seen, null)}
                                        title="Open this machine at its last frame before going silent"
                                        className="text-xs cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-gray-800 truncate pr-2">
                                                {machineLabel(s)}
                                            </span>
                                            <span className="text-gray-500 shrink-0">{elapsed(s.last_seen)}</span>
                                        </div>
                                        {s.ops_raised_by && (
                                            <div className="text-[10px] font-semibold text-blue-700">Operations raised · {s.ops_raised_by}</div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* TCU firmware reports: power, CAN link, battery … */}
                    <div className="bg-white rounded shadow p-5">
                        <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                            <svg className="w-4 h-4 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                            </svg>
                            Device Alerts
                        </h2>
                        {deviceAlerts.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">No device alerts in the last 24 hours.</p>
                        ) : (
                            <ul className="space-y-3 max-h-[clamp(14rem,32vh,30rem)] overflow-y-auto">
                                {deviceAlerts.map((a, i) => (
                                    <li key={i} onClick={() => openErrorViz(a.equipment_id, a.created_at, a.alarm_name)}
                                        title="Open this machine's visualization at the alert"
                                        className="text-xs cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-gray-800 truncate pr-2">{machineLabel(a)}</span>
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase shrink-0 ${
                                                a.severity === 'critical'
                                                    ? 'bg-red-100 text-red-700'
                                                    : a.severity === 'warning'
                                                        ? 'bg-orange-100 text-orange-700'
                                                        : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                                                {a.severity}
                                            </span>
                                        </div>
                                        <div className="text-[10px] text-gray-400 truncate" title={a.alarm_name}>
                                            {a.alarm_name} · {fmtIST(a.created_at)}
                                        </div>
                                        {a.ops_raised_by && (
                                            <div className="text-[10px] font-semibold text-blue-700">Operations raised · {a.ops_raised_by}</div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>

                {/* Row 4 — the floor */}
                <section className="bg-white rounded shadow p-5">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-sm font-semibold text-gray-700">Machines with the Service Team</h2>
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                            {workList.length} machine{workList.length === 1 ? '' : 's'}
                        </span>
                    </div>
                    {workList.length === 0 ? (
                        <div className="py-10 text-center text-sm text-gray-400 italic border-2 border-dashed border-gray-100 rounded">
                            No machines on the floor — every job is closed.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-100">
                                        {['Machine ID', 'Brand', 'Model', 'Job', 'Assigned Tech', 'Status', 'ETA'].map((h, i) => (
                                            <th key={h}
                                                className={`py-3 px-4 text-[10px] font-semibold text-gray-500 uppercase tracking-wider ${i === 6 ? 'text-right' : ''}`}>
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="text-xs text-gray-700">
                                    {workList.map(w => {
                                        const meta = STATUS_META[w.status] || { label: w.status, dot: 'bg-gray-400' };
                                        return (
                                            <tr key={w.ticket_id}
                                                onClick={() => navigate(`/service/tickets/${w.ticket_id}`)}
                                                className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer">
                                                <td className="py-3 px-4 font-medium text-gray-900">
                                                    {machineLabel(w)}
                                                </td>
                                                <td className="py-3 px-4">{w.brand || '—'}</td>
                                                <td className="py-3 px-4">{w.model || '—'}</td>
                                                <td className="py-3 px-4 max-w-[220px] truncate" title={w.description || ''}>
                                                    {w.job || 'Service'}
                                                </td>
                                                <td className="py-3 px-4">
                                                    {w.technician || <span className="text-gray-400">Unassigned</span>}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className="flex items-center gap-1.5">
                                                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                                                        {meta.label}
                                                    </span>
                                                </td>
                                                <td className={`py-3 px-4 text-right ${w.expected_completion ? 'text-gray-700' : 'text-red-500 font-medium'}`}>
                                                    {w.expected_completion ? fmtIST(w.expected_completion) : 'PENDING'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

export default ServiceControlPanel;
